import { ErrorBoundaryFallbackView } from '@/components/template/error-boundary';
import { GlobalLoadingOverlay } from '@/components/ui';
import { WebAlertHost } from '@/components/ui/alert-web-dialog';
import { SmartAppBanner } from '@/components/web';
import { useAppFonts } from '@/libs/fonts/use-app-fonts';
import { AuthStateEffect, configureGoogleSignIn } from '@/libs/firebase/auth';
import { startNetworkStatusListener } from '@/libs/network';
import { usePushNotifications } from '@/libs/notifications';
import { allowRestart } from '@/libs/revopush';
import { applyRevoPushSentryContext, sentryConfig } from '@/libs/sentry';
import { RootNavigator } from '@/navigations';
import { ComposeProviders, provider, QueryProvider } from '@/providers';
import { installWebInputStyles } from '@/utils/install-web-input-styles';
import { lockViewportZoom } from '@/utils/lock-viewport-zoom';
import { getFirestore } from '@react-native-firebase/firestore';
import * as Sentry from '@sentry/react-native';
import { useEffect, useState } from 'react';
import { Platform, StatusBar } from 'react-native';
import { KeyboardProvider } from 'react-native-keyboard-controller';
import { SafeAreaProvider } from 'react-native-safe-area-context';

Sentry.init(sentryConfig);
applyRevoPushSentryContext();

if (Platform.OS === 'android' || Platform.OS === 'ios') {
  allowRestart();
  getFirestore().settings({
    persistence: false,
  });
  configureGoogleSignIn();
}

function App() {
  const [initStatusBar, setInitStatusBar] = useState(false);

  // Inject the web @font-face at the root so fonts load on every entry point — including
  // deep-links that bypass the Splash screen (e.g. the OIDC `/sso-login/:uid` route, which
  // linking resolves straight into the UnAuth tree). SplashScreen also calls this for its
  // own gate; expo-font dedupes, so the extra call is harmless. No-op on native.
  useAppFonts();

  // Foreground push handling + device-token registration (native-only; no-op on web).
  usePushNotifications();

  useEffect(() => startNetworkStatusListener(), []);
  // Stop iOS Safari from auto-zooming into sub-16px inputs on focus (web-only; no-op elsewhere).
  useEffect(() => lockViewportZoom(), []);
  // Edge adds its own password reveal control; hide it so only the app toggle is shown.
  useEffect(() => installWebInputStyles(), []);

  return (
    <ComposeProviders
      providers={[provider(SafeAreaProvider), provider(KeyboardProvider), provider(QueryProvider)]}>
      {initStatusBar && (
        <StatusBar barStyle={'dark-content'} translucent backgroundColor="transparent" />
      )}
      <SmartAppBanner />
      <RootNavigator onReady={() => setInitStatusBar(true)} />
      <AuthStateEffect />
      <GlobalLoadingOverlay />
      <WebAlertHost />
    </ComposeProviders>
  );
}

export default Sentry.wrap(
  Sentry.withErrorBoundary(App, {
    fallback: <ErrorBoundaryFallbackView />,
  }),
);
